import { red } from '@mui/material/colors';
import React, { useContext, useState } from 'react';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { get_cod } from '../../../utils/cod';
import { displayLocation } from '../../../utils/locations';
import { displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';

import { GenericList } from '../../common/generic-list/generic-list';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import {
  getDeliveryStateColor,
  getDeliveryStateIcon,
  showDeliveryState,
} from '../../common/styles/order-states.style';
import { showPickupTaskChip } from '../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../common/styles/workflow-display';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import { OrderDeliveryTaskState } from '../../../enums/tasks';
import { showDeliveryTaskState } from '../../common/styles/delivery-task.styles';
import CachedIcon from '@mui/icons-material/Cached';

import OrderNotes from '../../common/order-notes/order-notes';
import { display_phone_number } from '../../../utils/phone_numbers';
import CustomerWithNavigate from '../../common/customer-with-navigate/customer-with-navigate';
import { format } from 'date-fns';
import IncompleteAddressError from '../../common/incomplete-address-error/incomplete-address-error';
import { AcUnit, Download, Info } from '@mui/icons-material';
import { GiCrackedGlass } from 'react-icons/gi';
import { OrgSettingsContext } from '../../../contexts/org-settings-context';
import { Box, Chip, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import CustomDialog from '../../common/custom-dialog/custom-dialog';
import { PrintWaybillsMultiSizes } from '../../common/bulk-action/print-waybills';
import { displayOrderSource } from '../../common/display-order-source/display-order-source';
import { QuickFilterScope } from '../../../enums/quick-filters';
import { IQuickFilter } from '../../../interfaces/common/quick-filters';
import { IFilterOption } from '../../common/filter-bar/types';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { UserType } from '../../../enums/users';
import { OrderIdWithPopover } from '../../common/order-id-with-popover/order-id-with-popover';
import { DeliveryState } from '../../../enums/orders';

export const relevant_fields = [
  'id',
  'order_id',
  'reference_id',
  'creation_date',
  'merchant',
  'customer',
  'price',
  'labels',
  'delivery_fee',
  'workflow',
  'number_of_packages',
  'customer_location',
  'merchant_location',
  'is_exchange',
  'delivery_state',
  'tracking_hash',
  'tracking_link',
  'description',
  'private_note',
  'task_notes',
  'public_note',
  'is_frozen',
  'is_fragile',
  'pop',
  'delivery_attempts_count',
  'is_critical',
  'last_delivery_task',
  'exchange_for',
  'return_reason',
  'labels',
  'source',
  'updated_by_us',
  'branch',
  'next_leg'
];

interface ResponsiveOrdersListingProps {
  ordersList: OrderType[];
  selectedOrders: Map<string, OrderType>;
  updateSelectedOrders: Updater<Map<string, OrderType>>;
  isLoading: boolean;
  sorting: SortingByColumnState;
  setSorting: React.Dispatch<React.SetStateAction<SortingByColumnState>>;
  applyQuickFilter: (quickFilter: IQuickFilter | null) => void;
  sourceView: string;
}

const ResponsiveOrdersListing = ({
  ordersList,
  selectedOrders,
  updateSelectedOrders,
  isLoading,
  sorting,
  setSorting,
  applyQuickFilter,
  sourceView
}: ResponsiveOrdersListingProps) => {
  const { color } = useContext(OrgSettingsContext)
  const { user } = useContext(AuthContext);
  return (
    <>
      <GenericList<OrderType>
        sourceView={sourceView}
        quickFiltersScope={QuickFilterScope.ORDER}
        applyQuickFilter={applyQuickFilter}
        items={ordersList}
        selectionEnabled
        selectedItems={selectedOrders}
        updateSelectedItems={updateSelectedOrders}
        isLoading={isLoading}
        fullWidth
        columns={[
          {
            header: (
              <>
                <b>
                  Order Id <br /> (Ref #)
                </b>
              </>
            ),
            id: 'order_id',
            display_body: (order) => (
              <OrderIdWithPopover order={order} />
            ),
          },
          {
            header: (
              <div
                onClick={() =>
                  setSorting({
                    ...sorting,
                    order:
                      sorting.order === 'increasing'
                        ? 'decreasing'
                        : 'increasing',
                  })
                }
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <b>
                  Creation Date
                  {sorting.order === 'increasing' ? (
                    <KeyboardArrowUpIcon fontSize="small" />
                  ) : (
                    <KeyboardArrowDownIcon fontSize="small" />
                  )}
                </b>
              </div>
            ),
            display_body: (order) => (
              <>
                {order.creation_date == undefined
                  ? 'Unknown'
                  : format(order.creation_date!, 'dd-MM-yyyy HH:mm')}

                {displayOrderSource(order.source)}
              </>
            ),
            id: 'creation_date',
          },
          {
            header: <b>Merchant</b>,
            id: 'merchant',
            display_body: (order) => (
              <a href={`/merchant/${order.merchant?.id}`}>
                <b><MerchantWithIcon merchant={order.merchant} /></b>
              </a>
            ),
          },
          {
            header: <b>Customer</b>,
            id: 'customer',
            display_body: (order) => (
              <>
                <CustomerWithNavigate customer={order.customer!} />
              </>
            ),
          },
          {
            header: <b>Attention</b>,
            id: 'attention',
            display_body: (order) => <>
            {order.is_critical && <IconWithText centered><Info color='warning' /> <b>Critical</b></IconWithText>}

                {order.is_fragile && <IconWithText centered><GiCrackedGlass size={16} color={color} /> <b color={color}>Fragile</b></IconWithText>}          
            </>
          },
          {
            header: <b>Workflow</b>,
            id: 'workflow',
            display_body: (order) => (
              <>
                {showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason)}
              </>
            ),
          },
          {
            header: <b>Delivery Attempts</b>,
            id: "attempts count",
            display_body: (order) => order.delivery_attempts_count
          },
          {
            header: <b>COD</b>,
            id: 'cod',
            display_body: (order) => displayMoney(get_cod(order)),
          },
          {
            header: <b>POP</b>,
            id: 'pop',
            display_body: (order) => displayMoney(order.pop),
          },
          {
            header: <b> Product Price </b>,
            id: 'product_price',
            hidden: user?.type != UserType.MERCHANT,
            display_body: (order) => order.product_price ? <>{order.product_price} $</> : ""
          },
          {
            header: <b> Branch </b>,
            id: 'branch',
            display_body: (order) => order.branch?.name ? (
              <Chip
                icon={<WarehouseOutlinedIcon sx={{ fontSize: 16 }} />}
                label={order.branch.name}
                size="small"
                sx={{
                  backgroundColor: 'rgba(158, 158, 158, 0.15)',
                  color: '#616161',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: '#757575' },
                }}
              />
            ) : null,
          },
          {
            header: <b> Next Step </b>,
            id: 'next_step',
            display_body: (order) => {
              // If order needs transfer to another branch
              if (order.next_leg?.destination) {
                return (
                  <Chip
                    icon={<WarehouseOutlinedIcon sx={{ fontSize: 16 }} />}
                    label={order.next_leg.destination.name}
                    size="small"
                    sx={{
                      backgroundColor: '#fff3e0',
                      color: '#e65100',
                      fontWeight: 500,
                      '& .MuiChip-icon': { color: '#e65100' },
                    }}
                  />
                );
              }
              
              // If order is in pickup states
              if ([DeliveryState.CREATED, DeliveryState.PICKUP_REQUESTED, DeliveryState.PICKUP_ASSIGNED, DeliveryState.PICKED_UP].includes(order.delivery_state as DeliveryState)) {
                return (
                  <Chip
                    icon={<InventoryOutlinedIcon sx={{ fontSize: 16 }} />}
                    label="Pickup"
                    size="small"
                    sx={{
                      backgroundColor: '#e3f2fd',
                      color: '#1565c0',
                      fontWeight: 500,
                      '& .MuiChip-icon': { color: '#1565c0' },
                    }}
                  />
                );
              }
              
              // Otherwise, last mile
              return (
                <Chip
                  icon={<LocalShippingOutlinedIcon sx={{ fontSize: 16 }} />}
                  label="Last Mile"
                  size="small"
                  sx={{
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                    fontWeight: 500,
                    '& .MuiChip-icon': { color: '#2e7d32' },
                  }}
                />
              );
            },
          },
          {
            header: <b>Notes</b>,
            id: 'notes',
            display_body: (order) => <>
              {!order.customer_location?.area && <IncompleteAddressError /> }
              <OrderNotes order={order} />
            </>
          },
          {
            header: <b>Status</b>,
            id: 'status',
            display_body: (order) => {
              return <>
                {showDeliveryState(order.delivery_state)}
                {order.last_delivery_task && order.last_delivery_task.failure_reason && <><br/><b>{order.last_delivery_task.failure_reason}</b></> }
              </>
            }
          }
        ]}
        getItemUID={(order) => order.id}
        rowConfigs={[
          {
            display_row: (order) => <b>Order #{order.order_id}</b>,
          },
          {
            display_row: (order) =>
              mobileDoubleItemsGrid([
                <div key="date">
                  {order.creation_date &&
                    new Date(order.creation_date).toLocaleDateString()}
                </div>,
                showWorkflow(order.workflow, order.is_exchange, order.exchange_for, order.return_reason),
              ]),
          },
          {
            display_row: (order) => (
              <IconWithText>
                <StorefrontIcon color="primary" />{' '}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/merchant/${order.merchant?.id}`);
                  }}
                >
                  <b>
                    {order.merchant?.name} {order.merchant?.phone_number}
                  </b>
                </div>
              </IconWithText>
            ),
          },
          {
            display_row: (order) => (
              <IconWithText>
                <PersonOutlineIcon color="primary" />{' '}
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(`/customer/${order.customer?.id}`);
                  }}
                >
                  <b>
                    {order.customer?.name} {order.customer?.phone_number}{' '}
                    {order.customer?.secondary_phone_number &&
                      `(${display_phone_number(order.customer.secondary_phone_number)})`}
                  </b>
                </div>
              </IconWithText>
            ),
          },
          {
            display_row: (order) => {
              return mobileDoubleItemsGrid([
                <div key="cod">
                  <b>COD: </b> {displayMoney(get_cod(order))}
                </div>,
                <div key="cod">
                  <b>POP: </b> {displayMoney(order.pop)}
                </div>,
              ]);
            },
          },
          {
            display_row: (order) => <>
              {!order.customer_location?.area && <IncompleteAddressError /> }
              <OrderNotes order={order} />
            </>,
          },
        ]}
        // cardFooter={(order) => { return { content: <>{order.delivery_state}</>, background: 'lightgrey' } }}
        cardIcon={(order) => <InventoryIcon color={'primary'} />}
        onCardClick={(order) => window.open(`/order/${order.id}`, '_blank')}
        cardFooter={(order) => {
          return {
            content: (
              <IconWithText centered>
                {getDeliveryStateIcon(order.delivery_state)}
                <>{order.delivery_state.replaceAll('_', ' ')}</>
              </IconWithText>
            ),
            background: getDeliveryStateColor(order.delivery_state),
          };
        }}
      />
    </>
    
  );
};

export default ResponsiveOrdersListing;
